import { useCallback, useEffect, useState } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { GlobalBannerView } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { LOCAL_STORAGE_KEYS } from '../../../constants';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { isUserTimezoneDifferent } from '../../../utils/date-helpers';

const localStorageManager = {
  key: LOCAL_STORAGE_KEYS.TIMEZONE_BANNER_DISMISSED,
  get: () => localStorage.getItem(LOCAL_STORAGE_KEYS.TIMEZONE_BANNER_DISMISSED) === 'true',
  set: (value: boolean) =>
    localStorage.setItem(LOCAL_STORAGE_KEYS.TIMEZONE_BANNER_DISMISSED, value ? String(value) : 'false'),
};

export const CalendarV3TimezoneBanner = () => {
  const { t } = useTranslation('schedule');
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const [bannerVisible, setShowBanner] = useState(false);
  const { accessibleLocationData } = useSchedulingLocationInfo();

  const dismissTimezoneBanner = useCallback(() => {
    localStorageManager.set(true);
    setShowBanner(false);
  }, []);

  useEffect(() => {
    if (!isScheduleV3FlagEnabled) return;

    const isDismissed = localStorageManager.get();
    if (isDismissed) return;

    const officeLocationTimezones = Object.values(accessibleLocationData).map((location) => location.timezone || '');
    setShowBanner(isUserTimezoneDifferent(officeLocationTimezones));
  }, [accessibleLocationData, isScheduleV3FlagEnabled]);

  if (!isScheduleV3FlagEnabled) return null;
  if (!bannerVisible) return null;

  return (
    <GlobalBannerView
      type='info'
      title={t('Times now displayed in location timezones')}
      message={t(
        "Your calendar now shows appointment times based on each location's timezone, with visual indicators to help you track time differences."
      )}
      isDismissible
      onDismiss={dismissTimezoneBanner}
    />
  );
};
